import React, { useState } from "react";
import { IInvoice } from "@/interfaces/Ibilling";
import InvoicePdfDialog from "@/components/InvoicePdfDialog";

interface InvoicesTabProps {
  invoices: IInvoice[];
}

const InvoicesTab: React.FC<InvoicesTabProps> = ({ invoices }) => {
  console.log("invoices", invoices);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(
    null
  );
  const [selectedInvoiceNumber, setSelectedInvoiceNumber] = useState<
    string | null
  >(null);
  const [isPdfDialogOpen, setIsPdfDialogOpen] = useState(false);

  const handleViewPdf = (invoiceId: string, invoiceNumber?: string) => {
    setSelectedInvoiceId(invoiceId);
    setSelectedInvoiceNumber(invoiceNumber || null);
    setIsPdfDialogOpen(true);
  };

  const handleClosePdfDialog = () => {
    setIsPdfDialogOpen(false);
    setSelectedInvoiceId(null);
    setSelectedInvoiceNumber(null);
  };
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(amount / 100); // Assuming amount is in cents
  };

  if (invoices.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-400 text-6xl mb-4">📄</div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          No invoices found
        </h3>
        <p className="text-gray-500">
          There are no invoices to display at the moment.
        </p>
      </div>
    );
  }

  return (
    <div className="w-full max-w-none">
      <div className="bg-white rounded-lg border border-gray-200">
        <table className="w-full table-fixed divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">
                Period
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">
                Invoice Number
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
                Total
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {invoices.map((invoice) => (
              <tr
                key={invoice.id}
                className="hover:bg-gray-50 transition-colors duration-150"
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    <div className="font-medium">
                      {formatDate(invoice.period_start)}
                    </div>
                    <div className="text-gray-500 text-xs">
                      to {formatDate(invoice.period_end)}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-mono text-gray-900 font-medium">
                    {invoice.number && invoice.number.length > 0
                      ? invoice.number
                      : "N/A"}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      invoice.status === "paid"
                        ? "bg-green-100 text-green-800 border border-green-200"
                        : invoice.status === "open"
                        ? "bg-yellow-100 text-yellow-800 border border-yellow-200"
                        : "bg-gray-100 text-gray-800 border border-gray-200"
                    }`}
                  >
                    {invoice.status.charAt(0).toUpperCase() +
                      invoice.status.slice(1)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">
                    {formatCurrency(invoice.total, invoice.currency)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex space-x-2">
                    <button
                      className="text-[#78C7D1] hover:text-[#4fa3b3] p-2 rounded-lg hover:bg-[#78C7D1] hover:bg-opacity-10 transition-colors duration-150"
                      onClick={() =>
                        handleViewPdf(invoice.id, invoice.number || undefined)
                      }
                      title="View PDF"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-5 h-5"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M2.25 12s3.75-6.75 9.75-6.75S21.75 12 21.75 12s-3.75 6.75-9.75 6.75S2.25 12 2.25 12z"
                        />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* PDF Dialog */}
      {selectedInvoiceId && (
        <InvoicePdfDialog
          isOpen={isPdfDialogOpen}
          onClose={handleClosePdfDialog}
          invoiceId={selectedInvoiceId}
          invoiceNumber={selectedInvoiceNumber || undefined}
        />
      )}
    </div>
  );
};

export default InvoicesTab;
